import React, { useState } from 'react';
import ExcelJS from 'exceljs';

interface PreauditLeftPanelProps {
  data: any;
  selectedId: string | null;
  onSelectRow: (id: string) => void;
  isCollapsed: boolean;
}

const PreauditLeftPanel: React.FC<PreauditLeftPanelProps> = ({ data, selectedId, onSelectRow, isCollapsed }) => {
  const { projectInfo, questions, statuses, reviewers } = data;
  const [searchQuery, setSearchQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState({
    status: [] as string[],
    reviewer: [] as string[]
  });

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  const closeDropdowns = () => {
    if (openDropdown) setOpenDropdown(null);
  };

  const toggleFilter = (category: keyof typeof activeFilters, value: string) => {
    setActiveFilters(prev => {
      const current = prev[category];
      if (current.includes(value)) {
        return { ...prev, [category]: current.filter(v => v !== value) };
      }
      return { ...prev, [category]: [...current, value] };
    });
  };

  const getFilterClass = (category: keyof typeof activeFilters) => {
    return `pa-filter-chip ${activeFilters[category].length > 0 ? 'active' : ''}`;
  };

  const filteredQuestions = questions?.filter((q: any) => {
    const matchesSearch = q.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = activeFilters.status.length === 0 || activeFilters.status.includes(q.status);
    const matchesReviewer = activeFilters.reviewer.length === 0 || activeFilters.reviewer.includes(q.reviewer);
    return matchesSearch && matchesStatus && matchesReviewer;
  }) || [];

  const handleExportXLS = async () => {
    if (!filteredQuestions || filteredQuestions.length === 0) return;

    const exportData = filteredQuestions.map((q: any) => ({
      No: q.no,
      Question: q.title,
      'Approved by': q.reviewer,
      Status: q.status
    }));

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Questions');
    worksheet.columns = [
      { header: 'No', key: 'No', width: 10 },
      { header: 'Question', key: 'Question', width: 50 },
      { header: 'Approved by', key: 'Approved by', width: 20 },
      { header: 'Status', key: 'Status', width: 15 },
    ];
    exportData.forEach((row) => worksheet.addRow(row));

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${projectInfo?.name || 'PreAudit'}_Questions.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} onClick={closeDropdowns}>
      {/* Header */}
      <div className="pa-header">
        <div className="pa-header-top">
          <div className="pa-dossier-info">
            <div className="pa-progress-circle" style={{ '--progress': `${projectInfo?.progressPercentage || 0}%` } as any}>
              <div className="pa-progress-inner">{projectInfo?.progressPercentage || 0}%</div>
            </div>
            <div className="pa-dossier-details">
              <h2>{projectInfo?.name}</h2>
              <p>Owner: {projectInfo?.owner}</p>
              <p className="pa-inconsistencies-count">{projectInfo?.totalQuestions} Questions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Export */}
      {!isCollapsed && (
        <div className="pa-filters">
          <button className="pa-export-btn" onClick={handleExportXLS}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
            Export XLS
          </button>
          
          <div className="pa-filter-right">
            <div className="pa-search-input-wrapper">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input 
                type="text" 
                className="pa-search-input" 
                placeholder="Search question" 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="pa-filter-dropdown-wrapper">
              <div className={getFilterClass('status')} onClick={(e) => { e.stopPropagation(); toggleDropdown('status'); }}>
                Status {activeFilters.status.length > 0 && <span className="badge">{activeFilters.status.length}</span>}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#94a3b8', marginLeft: '2px' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              {openDropdown === 'status' && (
                <div className="pa-filter-menu" onClick={e => e.stopPropagation()}>
                  {statuses?.map((val: string) => (
                    <label key={val} className="pa-filter-item">
                      <input
                        type="checkbox"
                        checked={activeFilters.status.includes(val)}
                        onChange={() => toggleFilter('status', val)}
                      />
                      {val}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="pa-filter-dropdown-wrapper">
              <div className={getFilterClass('reviewer')} onClick={(e) => { e.stopPropagation(); toggleDropdown('reviewer'); }}>
                Approved by {activeFilters.reviewer.length > 0 && <span className="badge">{activeFilters.reviewer.length}</span>}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#94a3b8', marginLeft: '2px' }}>
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              {openDropdown === 'reviewer' && (
                <div className="pa-filter-menu" onClick={e => e.stopPropagation()}>
                  {reviewers?.map((val: string) => (
                    <label key={val} className="pa-filter-item">
                      <input
                        type="checkbox"
                        checked={activeFilters.reviewer.includes(val)}
                        onChange={() => toggleFilter('reviewer', val)}
                      />
                      {val}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="pa-table-container">
        <table className="pa-table">
          <thead>
            <tr>
              <th style={{ width: '5%' }}>No</th>
              <th style={{ width: isCollapsed ? '95%' : '45%' }}>Question</th>
              {!isCollapsed && (
                <>
                  <th style={{ width: '30%' }}>Approved by</th>
                  <th style={{ width: '20%' }}>Status</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.map((row: any) => (
              <tr
                key={row.id}
                className={selectedId === row.id ? 'selected' : ''}
                onClick={() => onSelectRow(row.id)}
              >
                <td>{row.no}</td>
                <td>{row.title}</td>
                {!isCollapsed && (
                  <>
                    <td>
                      <div className="cg-reviewer-cell" style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '0 8px', color: '#334155' }}>
                        <span>{row.reviewer}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`pa-status ${row.status?.toLowerCase()}`}>
                        {row.status}
                      </span>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PreauditLeftPanel;
